import * as React from 'react';
import { useFormContext, type FieldPath, type FieldPathValue } from 'react-hook-form';
import { FormField } from '@/components/FormField';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { RoleType, User, PacketStatus } from '@promo/shared';
import { PacketFields } from './PacketFields';
import { SelectOption } from '@/components/ui/editable-select';
import { ComboboxOption } from '@/components/ui/editable-combobox';
import { TokenizedEmailAutocomplete } from '@/components/email/TokenizedEmailAutocomplete';

// Define interface matching the packet creation form schema
interface CreatePacketForm {
  candidateEmail: string;
  seniorLeaderId: string;
  currentTitle: string;
  targetTitle: string;
  lastPromotedDate: Date | null;
  year: string;
  summary: string;
  coManagerEmails?: string;
}

interface CreatePacketFormFieldsProps {
  isUpdating: boolean;
  className?: string;
}

export function CreatePacketFormFields({ isUpdating, className }: CreatePacketFormFieldsProps) {
  const { formState: { errors }, setValue, watch } = useFormContext<CreatePacketForm>();
  const [candidateEmailTokens, setCandidateEmailTokens] = React.useState<string[]>([]);
  const [candidateEmailDraft, setCandidateEmailDraft] = React.useState('');
  const [candidateInvalidTokens, setCandidateInvalidTokens] = React.useState<string[]>([]);
  const candidateEmailTokensRef = React.useRef<string[]>([]);
  const candidateEmailDraftRef = React.useRef('');
  const [coManagerEmailTokens, setCoManagerEmailTokens] = React.useState<string[]>([]);
  const [coManagerEmailDraft, setCoManagerEmailDraft] = React.useState('');
  const [coManagerInvalidTokens, setCoManagerInvalidTokens] = React.useState<string[]>([]);
  const coManagerEmailTokensRef = React.useRef<string[]>([]);
  const coManagerEmailDraftRef = React.useRef('');

  // Get current user (manager) for the packet
  const { session } = useAuth();
  const currentUser = session.user!;

  const composeEmailFieldValue = (emails: string[], draft: string) =>
    [...emails, draft.trim().toLowerCase()].filter(Boolean).join(', ');

  const updateCandidateEmailField = (emails: string[], draft: string) => {
    setValue('candidateEmail', (emails[0] ?? draft.trim()).toLowerCase(), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateCoManagerEmailsField = (emails: string[], draft: string) => {
    setValue('coManagerEmails', composeEmailFieldValue(emails, draft), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleCandidateEmailChange = (emails: string[]) => {
    const nextEmails = emails.slice(-1);
    candidateEmailTokensRef.current = nextEmails;
    setCandidateEmailTokens(nextEmails);
    setCandidateInvalidTokens([]);
    updateCandidateEmailField(nextEmails, candidateEmailDraftRef.current);
  };

  const handleCandidateDraftChange = (draft: string) => {
    candidateEmailDraftRef.current = draft;
    setCandidateEmailDraft(draft);
    setCandidateInvalidTokens([]);
    updateCandidateEmailField(candidateEmailTokensRef.current, draft);
  };

  const handleCandidateInvalidTokensChange = (tokens: string[]) => {
    setCandidateInvalidTokens(tokens);
    if (tokens.length > 0) {
      updateCandidateEmailField([], tokens[0]);
    }
  };

  const handleCoManagerEmailsChange = (emails: string[]) => {
    coManagerEmailTokensRef.current = emails;
    setCoManagerEmailTokens(emails);
    setCoManagerInvalidTokens([]);
    updateCoManagerEmailsField(emails, coManagerEmailDraftRef.current);
  };

  const handleCoManagerDraftChange = (draft: string) => {
    coManagerEmailDraftRef.current = draft;
    setCoManagerEmailDraft(draft);
    setCoManagerInvalidTokens([]);
    updateCoManagerEmailsField(coManagerEmailTokensRef.current, draft);
  };

  const handleCoManagerInvalidTokensChange = (tokens: string[]) => {
    setCoManagerInvalidTokens(tokens);
    if (tokens.length > 0) {
      updateCoManagerEmailsField(coManagerEmailTokensRef.current, tokens.join(', '));
    }
  };
  
  // Senior leader options
  const seniorLeadersQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.SENIOR_LEADER],
    queryFn: () => get<User[]>('/users', { role: RoleType.SENIOR_LEADER }),
  });
  const seniorLeaderOptions: SelectOption[] = seniorLeadersQuery.data?.map(u => ({ value: u.id, label: u.name || u.email })) || [];

  // Title options
  const titleList: ComboboxOption[] = [
    'Software Engineer 1', 'Software Engineer 2', 'Software Engineer 3', 'Sr. Software Engineer',
    'Staff Software Engineer','Principal Engineer','Distinguished Engineer','Manager Engineering',
    'Sr. Manager Engineering','Director Engineering','Sr. Director Engineering','Vice President Engineering',
    'Fellow Engineer, VP','SDET','Sr. SDET','Staff SDET','Principal SDET',
  ].map(label => ({ value: label, label }));

  // Assessment year options
  const currentYear = new Date().getFullYear();
  const assessmentYearOptions: SelectOption[] = [
    currentYear, currentYear + 1, currentYear + 2
  ].map(y => ({ value: String(y), label: String(y) }));

  // Handler to update form values with correct typing
  const handlePacketFieldChange = <Name extends FieldPath<CreatePacketForm>>(
    field: Name,
    value: FieldPathValue<CreatePacketForm, Name>
  ) => {
    setValue(field, value as FieldPathValue<CreatePacketForm, Name>, { shouldValidate: true });
  };

  return (
    <div className={className}>
      {/* Form-level error for unique roles */}
      {(errors.candidateEmail?.message === 'Candidate, manager, and senior leader must all be different users' ||
        errors.seniorLeaderId?.message === 'Candidate, manager, and senior leader must all be different users') && (
        <div className="text-red-500 text-sm mb-4">
          Candidate, manager, and senior leader must all be different users.
        </div>
      )}
      {/* Candidate Email Field */}
      <FormField 
        label="Candidate email" 
        htmlFor="candidateEmail"
        className="mb-6"
      >
        <TokenizedEmailAutocomplete
          id="candidateEmail"
          value={candidateEmailTokens}
          draftValue={candidateEmailDraft}
          onChange={handleCandidateEmailChange}
          onDraftChange={handleCandidateDraftChange}
          onInvalidTokensChange={handleCandidateInvalidTokensChange}
          placeholder="candidate@tebra.com"
          maxEmails={1}
          disabled={isUpdating}
        />
        {(candidateInvalidTokens.length > 0 || errors.candidateEmail) && (
          <p className="text-red-500 text-sm mt-1">
            {candidateInvalidTokens.length > 0
              ? `Invalid email format or domain: ${candidateInvalidTokens.join(', ')}`
              : errors.candidateEmail?.message as string}
          </p>
        )}
      </FormField>

      <FormField
        label="Co-manager emails"
        htmlFor="coManagerEmails"
        className="mb-6"
      >
        <TokenizedEmailAutocomplete
          id="coManagerEmails"
          value={coManagerEmailTokens}
          draftValue={coManagerEmailDraft}
          onChange={handleCoManagerEmailsChange}
          onDraftChange={handleCoManagerDraftChange}
          onInvalidTokensChange={handleCoManagerInvalidTokensChange}
          placeholder="name@tebra.com, other@tebra.com"
          disabled={isUpdating}
        />
        {(coManagerInvalidTokens.length > 0 || errors.coManagerEmails) && (
          <p className="text-red-500 text-sm mt-1">
            {coManagerInvalidTokens.length > 0
              ? `Invalid email format or domain: ${coManagerInvalidTokens.join(', ')}`
              : errors.coManagerEmails?.message as string}
          </p>
        )}
      </FormField>

      {/* Core packet fields */}
      <PacketFields
        values={{
          seniorLeaderId: watch('seniorLeaderId') || '',
          currentTitle: watch('currentTitle') || '',
          targetTitle: watch('targetTitle') || '',
          lastPromotedDate: watch('lastPromotedDate') || null,
          year: watch('year') || currentYear.toString(),
          summary: watch('summary') || '',
          managerId: currentUser.id,
        }}
        managerName={currentUser.name || currentUser.email}
        seniorLeaderOptions={seniorLeaderOptions}
        titleOptions={titleList}
        assessmentYearOptions={assessmentYearOptions}
        currentUserRole={RoleType.MANAGER}
        disabled={isUpdating}
        readOnly={false}
        isLoadingLeaders={seniorLeadersQuery.isLoading}
        errorLeaders={seniorLeadersQuery.error?.message}
        onFieldChange={handlePacketFieldChange}
        packetId={''}
        packetStatus={PacketStatus.DRAFT}
        decision={null}
        summaryMarkdownMode="edit"
        onSummaryMarkdownModeChange={() => {}} // No-op since we're not managing markdown state in create form
        isFocused
      />
    </div>
  );
}
